import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
// Añadir useCallback, useRef y useMemo a los imports
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import ImageUploader from "elements/ImageUploader";
import ReactQuill, { Quill } from 'react-quill';
import { ImageActions } from '@xeger/quill-image-actions';
import { ImageFormats } from '@xeger/quill-image-formats';
import ResizeModule from "@botom/quill-resize-module";
import 'react-quill/dist/quill.snow.css';

interface ImageUploadResponse {
  imageUrl: string;
}

interface BannerParams {
  bgType: 'color' | 'image';
  bgColor: string;
  bgImage: string;
  text: string;
  btnText: string;
  link: string;
}

interface BannerItem { id: string; html: string; params?: BannerParams; }

function parseBannerHtml(html: string): BannerParams {
  const trimmed = html.trimStart();
  // TYPE 1: imagen pura (<a><img></a>)
  if (trimmed.startsWith('<a ')) {
    const hrefMatch = html.match(/href="([^"]*)"/);
    const srcMatch  = html.match(/src="([^"]*)"/);
    return { bgType: 'image', bgImage: srcMatch?.[1] || '', link: hrefMatch?.[1] || '/', text: '', btnText: '', bgColor: '#125095' };
  }
  // TYPE 2: fondo con imagen CSS
  if (html.includes('background-image:url(')) {
    const imgMatch = html.match(/background-image:url\(([^)]+)\)/);
    const pMatch   = html.match(/<p [^>]*>([^<]*)<\/p>/);
    const aMatch   = html.match(/<a href="([^"]*)"[^>]*>([^<]*)<\/a>/);
    return { bgType: 'image', bgImage: imgMatch?.[1] || '', link: aMatch?.[1] || '/', text: pMatch?.[1] || '', btnText: aMatch?.[2] || '', bgColor: '#125095' };
  }
  // TYPE 3: fondo de color sólido
  const bgMatch = html.match(/background:([^;,]+)/);
  const pMatch  = html.match(/<p [^>]*>([^<]*)<\/p>/);
  const aMatch  = html.match(/<a href="([^"]*)"[^>]*>([^<]*)<\/a>/);
  return { bgType: 'color', bgColor: bgMatch?.[1]?.trim() || '#125095', bgImage: '', link: aMatch?.[1] || '/', text: pMatch?.[1] || '', btnText: aMatch?.[2] || '' };
}

// Cuando se carga un blog guardado, reemplaza <!--BANNER_START-->html<!--BANNER_END-->
// por marcadores de posición que Quill sí puede preservar: 📌 [BANNER:id]
function parseBanners(fullContent: string): { mainContent: string; banners: BannerItem[] } {
  const banners: BannerItem[] = [];
  let counter = 0;
  const mainContent = fullContent.replace(
    /<!--BANNER_START-->([\s\S]*?)<!--BANNER_END-->/g,
    (_, html) => {
      counter++;
      const id = `banner_loaded_${Date.now()}_${counter}`;
      banners.push({ id, html, params: parseBannerHtml(html) });
      return `<p>📌 [BANNER:${id}]</p>`;
    }
  ).trim();
  return { mainContent, banners };
}

// Registrar los módulos de imagen (true suprime el warning de sobreescritura)
Quill.register('modules/imageActions', ImageActions, true);
Quill.register('modules/imageFormats', ImageFormats, true);
Quill.register("modules/resize", ResizeModule, true);

// Definir formatos permitidos
const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'blockquote', 'code-block',
  'list', 'bullet',
  'align',
  'link', 'image', 'video', // Add video format
  'width', 'height', 'float',
  'color'  // Add color format
];

export default function BlogEditor(props: any) {
  const { open, setOpen, loadList, blog_id, source, company_id } = props;
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    blog_id: blog_id ? blog_id : null,
    title: '',
    content: '',
    image: '',
    meta_title: '',
    meta_description: '',
    alias: '',
    meta_keys: '',
    source: source || 'blog',
    company_id: company_id || null
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any>([]);
  const [bannerList, setBannerList] = useState<BannerItem[]>([]);
  const [pendingBannerPos, setPendingBannerPos] = useState<number | null>(null);

  const quillRef = useRef<any>(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file && quillRef.current) {
        const formData = new FormData();
        formData.append('image', file);

        try {
          await ApiRequest({
            url: '/blog/upload-image',
            method: 'POST',
            formData: true,
            body: formData,
            setResponse: (data: ImageUploadResponse) => {
              const editor = quillRef.current.getEditor();
              const range = editor.getSelection(true);
              editor.insertText(range.index, '\n');
              editor.insertEmbed(range.index, 'image', data.imageUrl);
              editor.setSelection(range.index + 2);
            }
          });
        } catch (error) {
          enqueueSnackbar('Error al subir la imagen', { variant: 'error' });
        }
      }
    };
  }, [enqueueSnackbar]);

  // Helper function to format YouTube URLs
  const formatYouTubeUrl = useCallback((url: string): string => {
    if (!url) return url;
    
    // Extract YouTube video ID from various URL formats
    let videoId = null;
    
    // Standard YouTube watch URL with v parameter
    const watchRegex = /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.+&v=)([^&]+)/;
    const watchMatch = url.match(watchRegex);
    
    // Short youtu.be URL
    const shortRegex = /youtu\.be\/([^?&]+)/;
    const shortMatch = url.match(shortRegex);
    
    // Embed URL
    const embedRegex = /youtube\.com\/embed\/([^?&]+)/;
    const embedMatch = url.match(embedRegex);
    
    if (watchMatch && watchMatch[1]) {
      videoId = watchMatch[1];
    } else if (shortMatch && shortMatch[1]) {
      videoId = shortMatch[1];
    } else if (embedMatch && embedMatch[1]) {
      videoId = embedMatch[1];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    return url; // Return original URL if not a YouTube URL or already formatted
  }, []);

  // Add video handler for the toolbar
  const videoHandler = useCallback(() => {
    const url = prompt('Ingrese la URL del video:');
    if (url && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      
      // Format the URL if it's a YouTube link
      const formattedUrl = formatYouTubeUrl(url);
      
      // Insert video embed
      editor.insertText(range.index, '\n');
      editor.insertEmbed(range.index, 'video', formattedUrl);
      editor.setSelection(range.index + 2);
    }
  }, [formatYouTubeUrl]);

  const [bannerDialog, setBannerDialog] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState<string | null>(null);
  const [bannerText, setBannerText] = useState('¿Necesita una enfermera especialista en heridas?');
  const [bannerBtnText, setBannerBtnText] = useState('Buscar Enfermera');
  const [bannerLink, setBannerLink] = useState('/enfermeras');
  const [bannerBgType, setBannerBgType] = useState<'color' | 'image'>('color');
  const [bannerBgColor, setBannerBgColor] = useState('#125095');
  const [bannerBgImage, setBannerBgImage] = useState('');
  const [bannerUploading, setBannerUploading] = useState(false);

  const handleBannerImageUpload = async (file: File) => {
    setBannerUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    await ApiRequest({
      url: '/blog/upload-image',
      method: 'POST',
      formData: true,
      body: formData,
      setResponse: (data: ImageUploadResponse) => {
        setBannerBgImage(data.imageUrl);
        setBannerUploading(false);
      },
      onError: () => {
        enqueueSnackbar('Error al subir la imagen de fondo', { variant: 'error' });
        setBannerUploading(false);
      }
    });
  };

  const [htmlDialog, setHtmlDialog] = useState(false);
  const [rawHtml, setRawHtml] = useState('');

  const insertRawHtml = () => {
    if (!quillRef.current || !rawHtml.trim()) return;
    const editor = quillRef.current.getEditor();
    editor.setContents([]);
    editor.clipboard.dangerouslyPasteHTML(0, rawHtml);
    setHtmlDialog(false);
    setRawHtml('');
  };

  // Captura la posición del cursor ANTES de abrir el diálogo (inserción nueva)
  const openBannerDialog = () => {
    setEditingBannerId(null);
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      setPendingBannerPos(range ? range.index : null);
    }
    setBannerDialog(true);
  };

  // Abre el diálogo con los datos del banner existente para editar
  const editBanner = (banner: BannerItem) => {
    if (banner.params) {
      setBannerBgType(banner.params.bgType);
      setBannerBgColor(banner.params.bgColor);
      setBannerBgImage(banner.params.bgImage);
      setBannerText(banner.params.text);
      setBannerBtnText(banner.params.btnText);
      setBannerLink(banner.params.link);
    }
    setEditingBannerId(banner.id);
    setBannerDialog(true);
  };

  // Elimina el banner del listado Y su marcador del editor Quill
  const removeBanner = (id: string) => {
    setBannerList(prev => prev.filter(b => b.id !== id));
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const text = editor.getText();
      const marker = `📌 [BANNER:${id}]`;
      const idx = text.indexOf(marker);
      if (idx !== -1) {
        // Eliminar también el salto de línea previo si existe
        const start = idx > 0 && text[idx - 1] === '\n' ? idx - 1 : idx;
        editor.deleteText(start, marker.length + (start < idx ? 1 : 0) + 1);
      }
    }
  };

  const insertBanner = () => {
    const id = `banner_${Date.now()}`;
    const num = bannerList.length + 1;
    const hasText = bannerText.trim() !== '';
    const hasBtn  = bannerBtnText.trim() !== '';

    let html: string;
    if (bannerBgType === 'image' && bannerBgImage) {
      if (!hasText && !hasBtn) {
        html =
          `<a class="eeh-banner" href="${bannerLink}" style="display:block;text-decoration:none;border-radius:10px;overflow:hidden;margin:20px 0;">` +
          `<img src="${bannerBgImage}" style="width:100%;display:block;border-radius:10px;" /></a>`;
      } else {
        html =
          `<div class="eeh-banner" style="position:relative;background-image:url(${bannerBgImage});background-size:cover;background-position:center;padding:48px 24px;text-align:center;border-radius:10px;margin:20px 0;overflow:hidden;">` +
          `<div style="position:absolute;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);border-radius:10px;"></div>` +
          `<div style="position:relative;z-index:1;">` +
          (hasText ? `<p style="color:#fff;font-size:18px;font-weight:600;margin:0 0 14px 0;">${bannerText}</p>` : '') +
          (hasBtn  ? `<a href="${bannerLink}" style="background:#fff;color:#125095;padding:10px 28px;border-radius:5px;text-decoration:none;font-weight:700;font-size:15px;">${bannerBtnText}</a>` : '') +
          `</div></div>`;
      }
    } else {
      html =
        `<div class="eeh-banner" style="background:${bannerBgColor};padding:24px 20px;text-align:center;border-radius:10px;margin:20px 0;">` +
        (hasText ? `<p style="color:#fff;font-size:18px;font-weight:600;margin:0 0 14px 0;">${bannerText}</p>` : '') +
        (hasBtn  ? `<a href="${bannerLink}" style="background:#fff;color:${bannerBgColor};padding:10px 28px;border-radius:5px;text-decoration:none;font-weight:700;font-size:15px;">${bannerBtnText}</a>` : '') +
        `</div>`;
    }

    const params: BannerParams = { bgType: bannerBgType, bgColor: bannerBgColor, bgImage: bannerBgImage, text: bannerText, btnText: bannerBtnText, link: bannerLink };

    if (editingBannerId) {
      // MODO EDICIÓN: solo actualiza el html y params del banner existente, sin tocar Quill
      setBannerList(prev => prev.map(b => b.id === editingBannerId ? { ...b, html, params } : b));
      setEditingBannerId(null);
      setBannerDialog(false);
      enqueueSnackbar('Banner actualizado', { variant: 'success', autoHideDuration: 2000 });
      return;
    }

    // MODO INSERCIÓN: insertar marcador en la posición del cursor
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const index = pendingBannerPos ?? editor.getLength() - 1;
      const marker = `📌 [BANNER:${id}]`;
      editor.insertText(index, '\n' + marker + '\n', 'user');
    }

    setBannerList(prev => [...prev, { id, html, params }]);
    setBannerDialog(false);
    enqueueSnackbar(`Banner #${num} insertado en la posición del cursor`, { variant: 'info', autoHideDuration: 2500 });
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }], 
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'], // Add video button to toolbar
        ['clean']
      ],
      handlers: {
        image: imageHandler,
        video: videoHandler // Add video handler
      }
    },
    imageActions: {
      modules: ['Resize', 'DisplaySize', 'Toolbar'],
      toolbar: [
        'float',
        'align',
        'resize'
      ]
    },
    imageFormats: {},
    resize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'], // Removed 'Toolbar' to remove +/- buttons
      overlayStyles: {
        position: 'absolute',
        boxSizing: 'border-box',
        border: '1px dashed #444',
      },
      handleStyles: {
        position: 'absolute',
        height: '12px',
        width: '12px',
        backgroundColor: 'white',
        border: '1px solid #777',
        boxSizing: 'border-box',
        opacity: '0.80',
      },
      displaySize: true,
      handleSides: ['right', 'bottom', 'corner'],
      allowDeselect: true,
      toolbarStyles: {
        position: 'absolute',
        top: '-12px',
        right: '0',
        left: '0',
        height: '0',
        minWidth: '100px',
        font: '12px/1.0 Arial, Helvetica, sans-serif',
        textAlign: 'center',
        color: '#333',
        boxSizing: 'border-box',
        cursor: 'default',
      },
      // Specify which elements can be resized
      imageSelectors: ['.ql-editor img'],
      videoSelectors: ['.ql-editor iframe.ql-video'],
      // This ensures videos are included in resizable elements
      selectionElements: ['img', 'iframe.ql-video'],
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler, videoHandler]);

  useEffect(() => {
    if (open) {
      setFiles([]);
      if (blog_id) {
        ApiRequest({
          url: '/blog/details',
          method: 'GET',
          query: { blog_id },
          setResponse: (response: any) => {
            const { mainContent, banners } = parseBanners(response.data.content || '');
            setFormValues({
              ...response.data,
              content: mainContent,
              source: response.data.source || source || 'blog',
              company_id: response.data.company_id || company_id || null
            });
            setBannerList(banners);
          }
        });
      } else {
        setFormValues(defaultValues);
        setBannerList([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, blog_id]);

  const isFormValid = () => {
    const hasContent = formValues.content !== '' && formValues.content !== '<p><br></p>';
    return formValues.title !== '' && (hasContent || bannerList.length > 0);
  };

  const handleSubmit = async () => {
    setLoading(true);

    const body = new FormData();
    // Reemplazar cada marcador 📌 [BANNER:id] por el HTML real del banner en su posición
    let finalContent = formValues.content;
    bannerList.forEach(({ id, html }) => {
      finalContent = finalContent.replace(
        new RegExp(`<p[^>]*>\\s*📌 \\[BANNER:${id}\\]\\s*</p>`, 'g'),
        `<!--BANNER_START-->${html}<!--BANNER_END-->`
      );
    });
    body.append('formValues', JSON.stringify({ ...formValues, content: finalContent }));
    if (files instanceof File) {
      body.append("image", files);
    }

    let responseHandled = false;
    await ApiRequest({
        url: "/blog/create",
        method: 'POST',
        formData: true,
        body: body,
        setResponse: (response: any) => {
            responseHandled = true;
            if (response?.status === 'error') {
                enqueueSnackbar(response.message || 'Error al guardar la entrada', { variant: 'error' });
                return;
            }
            enqueueSnackbar(blog_id ? 'Entrada actualizada correctamente' : 'Entrada creada correctamente', { variant: 'success' });
            setOpen(false);
            loadList();
            setFormValues(defaultValues);
        }
    });

    if (!responseHandled) {
        enqueueSnackbar('Error de conexión. Intente de nuevo.', { variant: 'error' });
    }

    setLoading(false);
  };

  const handleClose = () => {
    setFormValues(defaultValues);
    setFiles([]);
    setBannerList([]);
    setOpen(false);
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <div className="DialogContainer" >
        <div className="ModuleWrapper">
            <h2>{blog_id ? 'Editar entrada de blog' : 'Añadir entrada de blog'}</h2>
          
          <div className="input_group">
            <ImageUploader
              className="blog_image_uploader"
              image={formValues.image}
              setImage={(newImage) => setFormValues(prev => ({ ...prev, image: newImage }))}
              setFiles={setFiles}
            />
          </div>
          <div className="input_group">
            <TextField
              label="Título"
              variant="outlined"
              fullWidth
              value={formValues.title}
              onChange={(e) => setFormValues(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>
          
          <div className="input_group">
            {/* stopPropagation evita que el Dialog de MUI intercepte Enter/Backspace/flechas
                y rompa el editor Quill (especialmente bloques de código) */}
            <div onKeyDown={(e) => e.stopPropagation()}>
              <ReactQuill
                ref={quillRef}
                value={formValues.content}
                onChange={(content) => setFormValues(prev => ({ ...prev, content }))}
                style={{ height: '300px', marginBottom: '50px' }}
                modules={modules}
                formats={formats}
                theme="snow"
              />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button variant="outlined" size="small" onClick={openBannerDialog}>
                + Insertar Banner / CTA
              </Button>
              <Button
                variant="outlined" size="small" color="secondary"
                onClick={() => { setRawHtml(formValues.content); setHtmlDialog(true); }}
              >
                {'</>'} Pegar / Editar HTML
              </Button>
            </div>
            {(() => {
              const text = (formValues.content || '').replace(/<[^>]+>/g, '').trim();
              const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
              if (words === 0) return null;
              const color = words < 300 ? '#ef4444' : words < 600 ? '#f59e0b' : '#22c55e';
              const msg = words < 300 ? '— muy corto, mínimo recomendado 600' : words < 600 ? '— aceptable, apunta a 600+' : '— longitud ideal ✓';
              return <div style={{ fontSize: 11, color, marginTop: 6 }}>{words} palabras {msg}</div>;
            })()}

            {/* Banners posicionados — preview con botón eliminar */}
            {bannerList.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 8px 0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Banners insertados — aparecen donde está el marcador 📌 en el texto
                </p>
                {bannerList.map((banner, i) => (
                  <div key={banner.id} style={{ marginBottom: 12, position: 'relative' }}>
                    <div
                      dangerouslySetInnerHTML={{ __html: banner.html }}
                      style={{ pointerEvents: 'none', borderRadius: 8, overflow: 'hidden' }}
                    />
                    <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => editBanner(banner)}
                        style={{ background: 'rgba(18,80,149,0.85)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => removeBanner(banner.id)}
                        style={{ background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}
                      >
                        Eliminar
                      </button>
                    </div>
                    <span style={{ position: 'absolute', top: 8, left: 8, fontSize: 11, background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '2px 8px', borderRadius: 4 }}>
                      Banner #{i + 1}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="input_group">
            <h2>SEO</h2>
            <div>
              <TextField
                label="Título SEO"
                variant="outlined"
                fullWidth
                value={formValues.meta_title || ''}
                onChange={(e) => setFormValues(prev => ({ ...prev, meta_title: e.target.value }))}
                inputProps={{ maxLength: 70 }}
              />
              {(() => {
                const len = (formValues.meta_title || '').length;
                const color = len === 0 ? '#94a3b8' : len < 30 ? '#f59e0b' : len > 60 ? '#ef4444' : '#22c55e';
                const msg = len === 0 ? 'Recomendado: 50–60 caracteres' : len < 30 ? 'Muy corto — apunta a 50+' : len > 60 ? 'Muy largo — máximo 60' : 'Longitud ideal ✓';
                return <div style={{ fontSize: 11, color, marginTop: 4, marginBottom: 8 }}>{len}/60 — {msg}</div>;
              })()}
            </div>
            <div>
              <TextField
                label="Descripción SEO"
                variant="outlined"
                fullWidth
                multiline
                value={formValues.meta_description || ''}
                onChange={(e) => setFormValues(prev => ({ ...prev, meta_description: e.target.value }))}
                inputProps={{ maxLength: 200 }}
              />
              {(() => {
                const len = (formValues.meta_description || '').length;
                const color = len === 0 ? '#94a3b8' : len < 120 ? '#f59e0b' : len > 160 ? '#ef4444' : '#22c55e';
                const msg = len === 0 ? 'Recomendado: 120–160 caracteres' : len < 120 ? 'Muy corta — apunta a 120+' : len > 160 ? 'Muy larga — máximo 160' : 'Longitud ideal ✓';
                return <div style={{ fontSize: 11, color, marginTop: 4, marginBottom: 8 }}>{len}/160 — {msg}</div>;
              })()}
            </div>

            <TextField
              label="Alias (URL)"
              variant="outlined"
              fullWidth
              value={formValues.alias || ''}
              onChange={(e) => !blog_id && setFormValues(prev => ({ ...prev, alias: e.target.value }))}
              InputProps={{ readOnly: !!blog_id }}
              helperText={blog_id ? "El alias no se puede cambiar en publicaciones existentes" : "Ej: curacion-de-heridas-en-santiago (sin espacios, sin tildes)"}
            />

            <TextField
              label="Palabras Clave"
              variant="outlined"
              fullWidth
              multiline
              value={formValues.meta_keys || ''}
              onChange={(e) => setFormValues(prev => ({ ...prev, meta_keys: e.target.value }))}
              helperText="Separadas por coma. Ej: curación de heridas, enfermera a domicilio, ostomías Santiago"
            />
          </div>

        </div>
      </div>
      <div className="dialogButtons">
            <Button onClick={handleClose} variant="outlined">Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid() || loading}>
              {blog_id ? 'Actualizar entrada' : 'Crear entrada'}
            </Button>
          </div>
      
    </Dialog>

    <Dialog open={bannerDialog} onClose={() => { setBannerDialog(false); setEditingBannerId(null); }} maxWidth="sm" fullWidth>
      <DialogTitle>{editingBannerId ? 'Editar Banner / CTA' : 'Insertar Banner / CTA'}</DialogTitle>
      <DialogContent>

        {/* Tipo de fondo */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, marginTop: 4 }}>
          <Button
            size="small"
            variant={bannerBgType === 'color' ? 'contained' : 'outlined'}
            onClick={() => setBannerBgType('color')}
          >
            Color de fondo
          </Button>
          <Button
            size="small"
            variant={bannerBgType === 'image' ? 'contained' : 'outlined'}
            onClick={() => setBannerBgType('image')}
          >
            Imagen de fondo
          </Button>
        </div>

        {/* Selector de color */}
        {bannerBgType === 'color' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontSize: 14, color: '#555' }}>Color:</span>
            <input
              type="color"
              value={bannerBgColor}
              onChange={(e) => setBannerBgColor(e.target.value)}
              style={{ width: 48, height: 36, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: 2 }}
            />
            <span style={{ fontSize: 13, color: '#888' }}>{bannerBgColor}</span>
          </div>
        )}

        {/* Upload de imagen */}
        {bannerBgType === 'image' && (
          <div style={{ marginBottom: 12 }}>
            {bannerBgImage ? (
              <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', height: 90 }}>
                <img src={bannerBgImage} alt="fondo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  onClick={() => setBannerBgImage('')}
                  style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <div
                onClick={() => {
                  if (bannerUploading) return;
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = (e: any) => {
                    const file = e.target.files?.[0];
                    if (file) handleBannerImageUpload(file);
                  };
                  input.click();
                }}
                style={{
                  border: '2px dashed #cbd5e1', borderRadius: 8,
                  padding: '24px 16px', textAlign: 'center',
                  cursor: bannerUploading ? 'wait' : 'pointer',
                  color: '#94a3b8', fontSize: 14, background: '#f8fafc',
                }}
              >
                {bannerUploading ? 'Subiendo imagen...' : '📷 Clic para subir imagen de fondo'}
              </div>
            )}
          </div>
        )}

        <TextField
          label="Texto del banner (opcional)"
          fullWidth margin="normal"
          value={bannerText}
          onChange={(e) => setBannerText(e.target.value)}
          helperText={bannerBgType === 'image' ? 'Déjalo vacío si la foto ya tiene el texto incluido' : ''}
        />
        <TextField
          label="Texto del botón (opcional)"
          fullWidth margin="normal"
          value={bannerBtnText}
          onChange={(e) => setBannerBtnText(e.target.value)}
          helperText={bannerBgType === 'image' && !bannerBtnText.trim() ? 'Sin botón — toda la imagen será clickeable' : ''}
        />
        <TextField
          label="URL del enlace"
          fullWidth
          margin="normal"
          value={bannerLink}
          onChange={(e) => setBannerLink(e.target.value)}
          helperText="Ej: /enfermeras o https://especialistaenheridas.cl/enfermeras"
        />

        {/* Preview en vivo */}
        {bannerBgType === 'image' && bannerBgImage && !bannerText.trim() && !bannerBtnText.trim() ? (
          // Preview imagen pura clickeable
          <div style={{ marginTop: 16, borderRadius: 8, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}>
            <img src={bannerBgImage} alt="preview" style={{ width: '100%', display: 'block', borderRadius: 8 }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)', borderRadius: 8 }}>
              <span style={{ color: '#fff', fontSize: 12, background: 'rgba(0,0,0,0.5)', padding: '4px 10px', borderRadius: 4 }}>Toda la imagen será clickeable</span>
            </div>
          </div>
        ) : (
          <div style={{
            marginTop: 16, borderRadius: 8, overflow: 'hidden',
            position: 'relative', textAlign: 'center', padding: '24px 16px',
            ...(bannerBgType === 'image' && bannerBgImage
              ? { backgroundImage: `url(${bannerBgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : { background: bannerBgColor })
          }}>
            {bannerBgType === 'image' && bannerBgImage && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)' }} />
            )}
            <div style={{ position: 'relative', zIndex: 1 }}>
              {bannerText.trim() && (
                <p style={{ color: '#fff', margin: '0 0 12px 0', fontWeight: 600, fontSize: 16 }}>{bannerText}</p>
              )}
              {bannerBtnText.trim() && (
                <span style={{ background: '#fff', color: bannerBgType === 'color' ? bannerBgColor : '#125095', padding: '8px 20px', borderRadius: 4, fontWeight: 700, fontSize: 14 }}>
                  {bannerBtnText}
                </span>
              )}
              {!bannerText.trim() && !bannerBtnText.trim() && (
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Banner vacío</span>
              )}
            </div>
          </div>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setBannerDialog(false); setEditingBannerId(null); }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={insertBanner}
          disabled={bannerBgType === 'image' && (!bannerBgImage || bannerUploading)}
        >
          {editingBannerId ? 'Guardar cambios' : 'Insertar'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={htmlDialog} onClose={() => setHtmlDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>Pegar / Editar HTML</DialogTitle>
      <DialogContent>
        <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#555' }}>
          Pega aquí el HTML generado por la IA (con etiquetas <code>&lt;h1&gt;</code>, <code>&lt;h2&gt;</code>, <code>&lt;p&gt;</code>, etc.). Al hacer clic en <strong>Aplicar</strong> se renderizará correctamente en el editor.
        </p>
        <TextField
          multiline
          rows={14}
          fullWidth
          value={rawHtml}
          onChange={(e) => setRawHtml(e.target.value)}
          placeholder="<h1>Título del blog</h1><p>Contenido del párrafo...</p><h2>Subtítulo</h2>"
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHtmlDialog(false)}>Cancelar</Button>
        <Button variant="contained" onClick={insertRawHtml} disabled={!rawHtml.trim()}>
          Aplicar HTML al editor
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}